import "./AddDevice.scss";

import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { DeviceSelection } from "@/apps/partner/components/DeviceSelection/DeviceSelection";
import { PersonalDetails } from "@/apps/partner/components/PersonalDetails/PersonalDetails";
import { RateSelection } from "@/apps/partner/components/Rates/RateSelection";
import { SimSelection } from "@/apps/partner/components/SimSelection/SimSelection";
import { useScrollToFirstError } from "@/apps/partner/hooks/useScrollToFirstError";
import { AddDeviceFormType } from "@/apps/partner/types";
import { Button } from "@/components/Interface/Button/Button";
import { SCHWEIZ } from "@/constants/countries";
import { SalesPartnerCreateCustomerLanguageEnum } from "@/services/model";
import { addDeviceFormDataAtom } from "@/utils/atoms";

export const AddDevice = () => {
  const { t } = useTranslation("partnerPortal", {
    keyPrefix: "addDevice",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const setFormData = useSetAtom(addDeviceFormDataAtom);
  const existingFormData = useAtomValue(
    addDeviceFormDataAtom,
  ) as AddDeviceFormType | null;

  const formMethods = useForm<AddDeviceFormType>({
    shouldFocusError: false,
    reValidateMode: "onChange",
    mode: "onSubmit",
    defaultValues: existingFormData || {
      simType: undefined,
      iccid: "",
      puk: "",
      device: "",
      productId: undefined,
      mail: "",
      tariffName: "",
      tariffPrice: "",
      tariffSpeed: "",
      customer: {
        salutationEnum: "1",
        languageEnum: SalesPartnerCreateCustomerLanguageEnum.DE,
        firstName: "",
        lastName: "",
        street: "",
        houseNumber: "",
        postalDeliveryNotes: "",
        zip: "",
        city: "",
        countryCodeAlpha2: SCHWEIZ,
        contactPhoneNumber: "",
        birthday: "",
      },
      mnpData: {
        portedPhoneNumber: {
          phone: "",
        },
        currentProvider: {
          id: 0,
          description: "",
        },
        mnpTime: {
          id: 0,
          description: "",
        },
        mnpContractType: {
          id: 0,
          description: "",
        },
      },
      simAlias: "",
      onp: "false",
    },
  });

  formMethods.register("simType", {
    required: t("step1.required"),
  });

  formMethods.register("productId", {
    required: t("rateSelection.required"),
    valueAsNumber: true,
  });

  // Load existing form data when component mounts or when existingFormData changes
  useEffect(() => {
    if (existingFormData) {
      formMethods.reset(existingFormData);
    }
  }, [existingFormData, formMethods]);

  // Use the custom hook to scroll to first error
  useScrollToFirstError(
    formMethods.formState.errors,
    true,
    formMethods.formState.isSubmitting,
    formMethods.formState.submitCount,
  );

  // Handle scrolling to specific section based on navigation state
  useEffect(() => {
    const scrollToSection = location.state?.scrollToSection;
    if (scrollToSection) {
      // Use a small timeout to ensure the component has fully rendered
      const timer = setTimeout(() => {
        const targetElement = document.querySelector(
          `[data-section="${scrollToSection}"]`,
        );
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.state?.scrollToSection]);

  const handleSubmit = async (data: AddDeviceFormType) => {
    try {
      // Store form data in atom for Summary component
      setFormData(data);

      // Navigate to summary page
      navigate("/portal/add-device/summary");
    } catch (error) {
      console.error("Form submission failed:", error);
      // Handle error (show error message, etc.)
    }
  };

  return (
    <>
      <div id="add-device-wrapper" className="tile-width-wrapper">
        <div className="add-device-tile active">
          <FormProvider {...formMethods}>
            <div className="pt-10">
              <h1 className="mb-10 font-semibold text-primary-100">
                {t("step1.step")}
              </h1>
              <SimSelection />
              <hr className="my-20 border-[#CCD0FF]" />

              <div data-section="device">
                <h1 className="py-10 pt-10 font-semibold text-primary-100">
                  {t("step2.step")}
                </h1>
                <DeviceSelection />
              </div>

              <hr className="my-20 border-[#CCD0FF]" />

              <div data-section="tariff">
                <h1 className="py-10 font-semibold text-primary-100">
                  {t("step3.step")}
                </h1>
                <RateSelection />
              </div>

              <hr className="my-20 border-[#CCD0FF]" />

              <div data-section="personal">
                <h1 className="py-10 font-semibold text-primary-100">
                  {t("step4.step")}
                </h1>
                <PersonalDetails />
              </div>

              <div className="pt-10 md:flex md:justify-end">
                <Button
                  dataTestid="submit-onboarding-button"
                  className="accent w-full px-8 py-3 text-sm md:w-auto md:text-lg"
                  onClick={formMethods.handleSubmit(handleSubmit)}
                >
                  {t("continue")}
                </Button>
              </div>
            </div>
          </FormProvider>
        </div>
      </div>
    </>
  );
};
